import React, { useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTransactionAllocations, useTransactionDetail } from '../../../hooks/useApi';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbLabelContext';
import TransactionDetails from './TransactionDetails';
import { LoadingSpinner } from '../../ui/Loading';
import { parseBuyerScopeFromUrl, transactionsListPath } from './transactionListUrl';
import { transactionNeedsAllocationsFetch } from '../../../utils/transactionAllocationSummary';

const TransactionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const numericId = id ? parseInt(id, 10) : NaN;
  const valid = Number.isFinite(numericId) && numericId > 0;

  const buyerScope = parseBuyerScopeFromUrl(searchParams.get('scope'));
  const listSearch = searchParams.get('search') || undefined;
  const backTo = transactionsListPath({ buyerScope, search: listSearch });

  const { data, isLoading, error, refetch } = useTransactionDetail(valid ? numericId : 0);
  const transaction = data?.data?.transaction;

  const needsAllocations = useMemo(
    () => (transaction ? transactionNeedsAllocationsFetch(transaction) : false),
    [transaction],
  );

  const {
    data: allocationsData,
    isLoading: allocationsLoading,
  } = useTransactionAllocations(valid ? numericId : 0, needsAllocations);

  const allocations = allocationsData?.data?.allocations ?? [];

  useBreadcrumbLabel(
    transaction ? transaction.transactionId || `#${transaction.id}` : undefined,
  );

  const back = () => navigate(backTo);

  if (!valid) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Invalid transaction id.</p>
        <Link to={backTo} className="text-sm font-medium text-primary underline-offset-4 hover:underline">
          Back to payment transactions
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">Could not load this transaction.</p>
        <button
          type="button"
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          onClick={back}
        >
          Back to payment transactions
        </button>
      </div>
    );
  }

  return (
    <TransactionDetails
      transaction={transaction}
      variant="page"
      onClose={back}
      onTransactionRefresh={() => {
        void refetch();
      }}
      listHref={backTo}
      allocations={needsAllocations ? allocations : undefined}
      allocationsLoading={needsAllocations && allocationsLoading}
    />
  );
};

export default TransactionDetailPage;
